<script>
import ConversationApi from 'dashboard/api/inbox/conversation';
import OrdersAPI from 'dashboard/api/orders';
import ProductsAPI from 'dashboard/api/products';
import { emitter } from 'shared/helpers/mitt';

export default {
  name: 'AiOrderFormModal',
  props: {
    show: { type: Boolean, default: false },
    conversationId: { type: [Number, String], required: true },
  },
  emits: ['close', 'created'],
  data() {
    return {
      loading: false,
      submitting: false,
      // date/time controls
      deliveryDateISO: '',
      deliveryTimeStr: '',
      // manual customer/product search states
      searchCustomerPhone: '',
      searchCustomerEmail: '',
      searchCustomerLoading: false,
      searchCustomerResult: null,
      searchCustomerError: '',
      searchProductQuery: '',
      searchProductLoading: false,
      searchProductResults: [],
      searchProductError: '',
      form: {
        full_name: '',
        phone_number: '',
        email: '',
        shipping_full_name: '',
        shipping_phone_number: '',
        shipping_address: '',
        delivery_date: '',
        delivery_hour: null,
        delivery_minute: null,
        billing_is_shipping: true,
        customer_note: '',
        items: [],
      },
    };
  },
  computed: {
    totalAmount() {
      const items = Array.isArray(this.form.items) ? this.form.items : [];
      return items.reduce((sum, it) => {
        const price = parseInt(it.price, 10) || 0;
        const qty = parseInt(it.quantity, 10) || 0;
        return sum + (price * qty);
      }, 0);
    },
    totalAmountVnd() {
      try {
        return new Intl.NumberFormat('vi-VN').format(this.totalAmount) + ' ₫';
      } catch (e) {
        return `${this.totalAmount} ₫`;
      }
    },
  },
  watch: {
    show(newVal) {
      if (newVal) {
        this.$nextTick(() => this.initDateTimeFromForm());
      }
    }
  },
  methods: {
    initDateTimeFromForm() {
      const ddmmyyyy = (this.form.delivery_date || '').trim();
      if (ddmmyyyy && /\d{2}\/\d{2}\/\d{4}/.test(ddmmyyyy)) {
        const [d, m, y] = ddmmyyyy.split('/').map(s => parseInt(s, 10));
        const mm = String(m).padStart(2, '0');
        const dd = String(d).padStart(2, '0');
        this.deliveryDateISO = `${y}-${mm}-${dd}`;
      } else {
        this.deliveryDateISO = '';
      }
      const hour = Number.isInteger(this.form.delivery_hour) ? this.form.delivery_hour : null;
      const minute = Number.isInteger(this.form.delivery_minute) ? this.form.delivery_minute : null;
      if (hour != null && minute != null) {
        const hh = String(hour).padStart(2, '0');
        const mm = String(minute).padStart(2, '0');
        this.deliveryTimeStr = `${hh}:${mm}`;
      } else {
        this.deliveryTimeStr = '';
      }
    },
    onChangeDateISO(e) {
      const val = (e?.target?.value || '').trim();
      this.deliveryDateISO = val;
      if (val) {
        const [y, m, d] = val.split('-').map(s => parseInt(s, 10));
        const dd = String(d).padStart(2, '0');
        const mm = String(m).padStart(2, '0');
        this.form.delivery_date = `${dd}/${mm}/${y}`;
      } else {
        this.form.delivery_date = '';
      }
    },
    onChangeTimeStr(e) {
      const val = (e?.target?.value || '').trim();
      this.deliveryTimeStr = val;
      if (val && /\d{2}:\d{2}/.test(val)) {
        const [hh, mm] = val.split(':').map(s => parseInt(s, 10));
        this.form.delivery_hour = hh;
        this.form.delivery_minute = mm;
      } else {
        this.form.delivery_hour = null;
        this.form.delivery_minute = null;
      }
    },
    async fetchFromAI() {
      try {
        this.loading = true;
        const res = await ConversationApi.aiOrderInfo({ conversationId: this.conversationId });
        
        if (res?.data?.success) {
          const info = res.data.order_info;
          if (info && typeof info === 'object') {
            this.form = { ...this.form, ...info };
            this.$nextTick(() => this.initDateTimeFromForm());
            await this.autoEnrichAfterAI(info);
            emitter.emit('newToastMessage', { message: 'Đã lấy thông tin từ AI thành công', action: { type: 'success' } });
          } else {
            emitter.emit('newToastMessage', { message: 'AI không thể trích xuất thông tin đơn hàng từ cuộc trò chuyện', action: { type: 'warning' } });
          }
        } else {
          emitter.emit('newToastMessage', { message: res?.data?.error || 'Lỗi lấy thông tin AI', action: { type: 'error' } });
        }
      } catch (e) {
        console.error('[AiOrderFormModal] fetchFromAI error:', e);
        emitter.emit('newToastMessage', { message: e?.response?.data?.error || e.message || 'Lỗi lấy thông tin AI', action: { type: 'error' } });
      } finally {
        this.loading = false;
      }
    },
    async autoEnrichAfterAI(info) {
      try {
        const phone = info.phone_number;
        const email = info.email;
        if (phone || email) {
          try {
            const r = await ConversationApi.searchKkfCustomer({ conversationId: this.conversationId, phoneNumber: phone, email });
            if (r?.data?.success && r.data.data) {
              const c = r.data.data;
              this.form.full_name ||= c.FullName || '';
              this.form.shipping_full_name ||= c.FullName || '';
              this.form.shipping_address ||= c.Address || '';
              this.form.phone_number ||= c.PhoneNumber || '';
              this.form.email ||= c.Email || '';
              console.log('[AiOrderFormModal] Auto-enriched customer info from KKF');
            }
          } catch (e) {
            console.warn('[AiOrderFormModal] Failed to auto-enrich customer info:', e.message);
          }
        }
        // Enrich product names if AI returned sku/product_id
        const items = Array.isArray(this.form.items) ? this.form.items : [];
        const tasks = items.map(async it => {
          const item = it || {};
          const keyword = (item.sku || item.product_id || '').toString();
          if (!keyword) return null;
          try {
            const rs = await ProductsAPI.search({ q: keyword });
            const first = rs?.data?.data?.[0];
            if (first) {
              item.name = item.name || first.Name || first.name;
              item.price = item.price || first.Price || first.price;
              item.picture_url = item.picture_url || first.PictureUrl || first.picture_url;
            }
          } catch (e) {
            console.warn('[AiOrderFormModal] Failed to enrich product info for:', keyword);
            return null;
          }
          return null;
        });
        await Promise.all(tasks);
      } catch (err) {
        // ignore enrich errors; keep form as-is
        console.warn('[AiOrderFormModal] autoEnrichAfterAI error', err);
      }
    },
    async manualSearchCustomer() {
      this.searchCustomerError = '';
      this.searchCustomerResult = null;
      const phone = (this.searchCustomerPhone || '').trim();
      const email = (this.searchCustomerEmail || '').trim();
      if (!phone && !email) {
        this.searchCustomerError = 'Nhập số điện thoại hoặc email';
        return;
      }
      this.searchCustomerLoading = true;
      try {
        const rs = await ConversationApi.searchKkfCustomer({ conversationId: this.conversationId, phoneNumber: phone, email });
        if (rs?.data?.success) {
          this.searchCustomerResult = rs.data.data;
          emitter.emit('newToastMessage', { message: 'Tìm thấy thông tin khách hàng', action: { type: 'success' } });
        } else {
          this.searchCustomerError = rs?.data?.error || 'Không tìm thấy khách hàng';
          emitter.emit('newToastMessage', { message: 'Không tìm thấy khách hàng với thông tin này', action: { type: 'warning' } });
        }
      } catch (e) {
        console.error('[AiOrderFormModal] manualSearchCustomer error:', e);
        this.searchCustomerError = e?.response?.data?.error || e.message || 'Lỗi tìm kiếm khách hàng';
        emitter.emit('newToastMessage', { message: 'Lỗi tìm kiếm khách hàng', action: { type: 'error' } });
      } finally {
        this.searchCustomerLoading = false;
      }
    },
    applyCustomerInfo(c) {
      if (!c) return;
      const name = c.FullName || c.full_name || '';
      const phone = c.PhoneNumber || c.phone_number || '';
      const email = c.Email || c.email || '';
      const addr = c.Address || c.address || '';
      // Overwrite to ensure user sees selected info immediately
      this.form.full_name = name;
      this.form.shipping_full_name = name;
      this.form.phone_number = phone;
      this.form.shipping_phone_number = phone;
      this.form.email = email;
      this.form.shipping_address = addr;
    },
    async manualSearchProducts() {
      this.searchProductError = '';
      this.searchProductResults = [];
      const q = (this.searchProductQuery || '').trim();
      if (!q) {
        this.searchProductError = 'Nhập từ khóa hoặc SKU';
        return;
      }
      this.searchProductLoading = true;
      try {
        const rs = await ProductsAPI.search({ q });
        if (rs?.data?.success) {
          this.searchProductResults = Array.isArray(rs.data.data) ? rs.data.data : [];
          if (this.searchProductResults.length === 0) {
            emitter.emit('newToastMessage', { message: 'Không tìm thấy sản phẩm nào', action: { type: 'warning' } });
          } else {
            emitter.emit('newToastMessage', { message: `Tìm thấy ${this.searchProductResults.length} sản phẩm`, action: { type: 'success' } });
          }
        } else {
          this.searchProductError = rs?.data?.error || 'Không có kết quả';
          emitter.emit('newToastMessage', { message: 'Không tìm thấy sản phẩm nào', action: { type: 'warning' } });
        }
      } catch (e) {
        console.error('[AiOrderFormModal] manualSearchProducts error:', e);
        this.searchProductError = e?.response?.data?.error || e.message || 'Lỗi tìm sản phẩm';
        emitter.emit('newToastMessage', { message: 'Lỗi tìm kiếm sản phẩm', action: { type: 'error' } });
      } finally {
        this.searchProductLoading = false;
      }
    },
    addSearchedProduct(p) {
      if (!p) return;
      const productId = p.Id || p.id || null;
      const sku = p.Sku || p.sku || '';
      const price = p.Price || p.price || null;
      const name = p.Name || p.name || '';
      const picture_url = p.PictureUrl || p.picture_url || '';
      this.form.items.push({ product_id: productId, sku, quantity: 1, price, name, picture_url });
    },
    validateForm() {
      const f = this.form;
      const errors = [];
      if (!String(f.full_name || '').trim()) errors.push('Họ tên');
      if (!String(f.shipping_address || '').trim()) errors.push('Địa chỉ giao');
      if (!String(f.delivery_date || '').trim()) errors.push('Ngày giao');
      const hourOk = Number.isInteger(f.delivery_hour) || (f.delivery_hour === 0);
      if (!hourOk) errors.push('Giờ giao');
      const items = Array.isArray(f.items) ? f.items : [];
      const validItem = items.some(it => ((it.product_id || it.sku) && (parseInt(it.quantity, 10) > 0)));
      if (!validItem) errors.push('Sản phẩm');
      return errors;
    },
    addItem() {
      this.form.items.push({ product_id: null, sku: '', quantity: 1 });
    },
    removeItem(i) {
      this.form.items.splice(i, 1);
    },
    async submit() {
      try {
        this.submitting = true;
        const errs = this.validateForm();
        if (errs.length) {
          emitter.emit('newToastMessage', { message: `Thiếu: ${errs.join(', ')}`, action: { type: 'error' } });
          return;
        }
        
        const res = await OrdersAPI.create({ conversationId: this.conversationId, orderInfo: this.form });
        
        if (res?.data?.success) {
          emitter.emit('newToastMessage', { message: 'Đã lên đơn thành công', action: { type: 'success' } });
          this.$emit('created', res.data?.data);
          this.$emit('close');
        } else {
          emitter.emit('newToastMessage', { message: res?.data?.error || 'Lỗi tạo đơn', action: { type: 'error' } });
        }
      } catch (e) {
        console.error('[AiOrderFormModal] submit error:', e);
        emitter.emit('newToastMessage', { message: e?.response?.data?.error || e.message || 'Lỗi tạo đơn', action: { type: 'error' } });
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<template>
  <woot-modal :show="show" :on-close="() => $emit('close')" size="w-[95vw] h-[90vh] max-w-[1200px] max-h-[90vh]">
    <div class="flex flex-col h-full bg-white dark:bg-slate-900 overflow-hidden relative">
      <!-- Loading overlay -->
      <div v-if="loading || submitting" class="absolute inset-0 bg-white/80 dark:bg-slate-900/80 flex items-center justify-center z-50">
        <div class="flex flex-col items-center gap-3">
          <woot-spinner size="medium" />
          <p class="text-sm text-n-slate-11 dark:text-slate-300">
            {{ loading ? 'Đang lấy thông tin từ AI...' : 'Đang tạo đơn hàng...' }}
          </p>
        </div>
      </div>
      <!-- Header -->
      <div class="flex items-center justify-between p-5 border-b border-n-slate-6 bg-white dark:bg-slate-900">
        <div>
          <h3 class="text-lg font-semibold text-n-slate-12 dark:text-slate-100">Tạo đơn mới</h3>
          <p class="text-xs text-n-slate-11 dark:text-slate-300">Lấy thông tin bằng AI hoặc nhập tay. Tìm khách/SKU ở panel trái.</p>
        </div>
        <div></div>
      </div>

      <!-- Body -->
      <div class="flex-1 grid grid-cols-3 gap-4 p-5 overflow-auto bg-n-slate-2 dark:bg-slate-800">
        <!-- Left column: search panels -->
        <div class="col-span-1 space-y-4">
          <div class="p-4 rounded-lg border border-n-slate-6 bg-white dark:bg-slate-900">
            <div class="text-sm font-semibold text-n-slate-12 dark:text-slate-100 mb-3">Tra khách hàng KKF</div>
            <div class="grid grid-cols-2 gap-2 mb-2">
              <woot-input v-model="searchCustomerPhone" placeholder="Số điện thoại" @keyup.enter="manualSearchCustomer" />
              <woot-input v-model="searchCustomerEmail" placeholder="Email" @keyup.enter="manualSearchCustomer" />
            </div>
            <div class="flex items-center gap-2">
              <woot-button size="small" :is-loading="searchCustomerLoading" @click="manualSearchCustomer">Tìm</woot-button>
              <span v-if="searchCustomerError" class="text-xs text-red-500">{{ searchCustomerError }}</span>
            </div>
            <div v-if="searchCustomerResult" class="mt-3 p-3 bg-n-teal-2 border border-n-teal-6 rounded-md text-xs text-n-teal-12">
              <div class="font-semibold mb-1">Tìm thấy khách hàng</div>
              <div class="space-y-1 text-n-teal-11">
                <div>Tên: <span class="font-medium">{{ searchCustomerResult.FullName || searchCustomerResult.full_name }}</span></div>
                <div>SĐT: {{ searchCustomerResult.PhoneNumber || searchCustomerResult.phone_number }}</div>
                <div>Địa chỉ: {{ searchCustomerResult.Address || searchCustomerResult.address }}</div>
              </div>
              <div class="mt-2 text-right">
                <woot-button size="tiny" variant="smooth" @click="applyCustomerInfo(searchCustomerResult)">Chọn & điền</woot-button>
              </div>
            </div>
          </div>

          <div class="p-4 rounded-lg border border-n-slate-6 bg-white dark:bg-slate-900">
            <div class="text-sm font-semibold text-n-slate-12 dark:text-slate-100 mb-3">Tìm sản phẩm KKF</div>
            <div class="flex gap-2 mb-2">
              <woot-input v-model="searchProductQuery" placeholder="Từ khóa hoặc SKU" @keyup.enter="manualSearchProducts" />
              <woot-button size="small" :is-loading="searchProductLoading" @click="manualSearchProducts">Tìm</woot-button>
            </div>
            <div v-if="searchProductError" class="text-xs text-red-500 mt-1">{{ searchProductError }}</div>
            <div v-if="searchProductResults.length" class="max-h-56 overflow-auto space-y-2">
              <div v-for="(p, i) in searchProductResults" :key="i" class="p-3 bg-white dark:bg-slate-900 border border-n-slate-6 rounded-lg hover:shadow-sm flex items-center justify-between gap-3">
                <div class="min-w-0">
                  <div class="text-sm font-medium text-n-slate-12 dark:text-slate-100 truncate">{{ (p.Name || p.name) }}</div>
                  <div class="text-xs text-n-slate-10 dark:text-slate-300">SKU: {{ p.Sku || p.sku }}</div>
                </div>
                <div class="flex items-center gap-2">
                  <span class="text-sm font-semibold text-n-teal-11">{{ (p.Price || p.price) ? new Intl.NumberFormat('vi-VN').format(p.Price || p.price) + ' ₫' : '' }}</span>
                  <woot-button size="tiny" variant="smooth" @click="addSearchedProduct(p)">Chọn & thêm</woot-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right column: form -->
        <div class="col-span-2">
          <div class="p-4 rounded-lg border border-n-slate-6 dark:border-slate-700 bg-white dark:bg-slate-900">
            <div class="grid grid-cols-2 gap-3">
            <woot-input v-model="form.full_name" label="Họ tên (Billing)" />
            <woot-input v-model="form.shipping_full_name" label="Họ tên (Nhận)" />
            <woot-input v-model="form.phone_number" label="Số điện thoại" />
            <woot-input v-model="form.email" label="Email" />
            <woot-input v-model="form.shipping_phone_number" label="SĐT người nhận" />
            <woot-input v-model="form.shipping_address" label="Địa chỉ giao" class="col-span-2" />
            <div class="grid grid-cols-2 gap-2">
              <label class="text-xs text-n-slate-11 dark:text-slate-300">Ngày giao
                <input :value="deliveryDateISO" type="date" class="mt-1 w-full rounded border border-n-slate-6 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm" @change="onChangeDateISO" />
              </label>
              <label class="text-xs text-n-slate-11 dark:text-slate-300">Giờ giao
                <input :value="deliveryTimeStr" type="time" class="mt-1 w-full rounded border border-n-slate-6 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm" @change="onChangeTimeStr" />
              </label>
            </div>
            <woot-input v-model="form.customer_note" label="Ghi chú" class="col-span-2" />
            </div>
          </div>

          <div class="mt-4 p-4 rounded-lg border border-n-slate-6 dark:border-slate-700 bg-white dark:bg-slate-900">
            <h4 class="text-sm font-medium mb-2 text-n-slate-12 dark:text-slate-100">Sản phẩm</h4>
            <div v-for="(it, idx) in form.items" :key="idx" class="grid grid-cols-6 gap-2 items-end mb-2">
              <div class="col-span-1 flex items-center justify-center">
                <img v-if="it.picture_url" :src="it.picture_url" alt="thumb" class="w-12 h-12 object-cover rounded border border-n-slate-6 dark:border-slate-700" />
                <div v-else class="w-12 h-12 rounded bg-n-slate-4 dark:bg-slate-700 border border-n-slate-6 dark:border-slate-700 flex items-center justify-center text-xs text-n-slate-8 dark:text-slate-300">IMG</div>
              </div>
              <woot-input v-model="it.product_id" label="ProductId" />
              <woot-input v-model="it.sku" label="SKU" />
              <woot-input v-model.number="it.quantity" type="number" label="SL" />
              <woot-input v-model.number="it.price" type="number" label="Giá" />
              <woot-button variant="link" color-scheme="danger" @click="removeItem(idx)">Xóa</woot-button>
            </div>
          </div>
          <div class="mt-2 text-right text-sm font-medium"></div>
        </div>
      </div>

      <!-- Footer -->
      <div class="p-5 border-t border-n-slate-6 bg-white dark:bg-slate-900 flex items-center justify-between gap-2">
        <div class="text-sm font-medium text-n-slate-12 dark:text-slate-100">Tổng: {{ totalAmountVnd }}</div>
        <div class="flex gap-2">
          <woot-button :is-loading="loading" variant="hollow" @click="fetchFromAI">Lấy thông tin</woot-button>
          <woot-button :is-loading="submitting" color-scheme="primary" @click="submit">Lên đơn</woot-button>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<style scoped>
</style>


